import { find } from 'lodash';
import { PropTypes } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Controller, useFormContext } from 'react-hook-form';
import { RequiredWrapper } from '../formValidation';
import { selectCarMakes, selectCarModels } from '../../store/carModelReducer';
import {
  fetchCarMakes as fetchCarMakesAction,
  fetchCarModels as fetchCarModelsAction,
} from '../actions';
import SuggestedTextPicker from './SuggestedTextPicker';

const CarModelSelect = ({
  fetchCarMakes,
  fetchCarModels,
  makes,
  models,
  makeProperty,
  modelProperty,
}) => {
  const { control, watch, setValue, getValues } = useFormContext();
  const [previousMake, setPreviousMake] = React.useState(null);
  const [isSelectingMake, setIsSelectingMake] = React.useState(false);

  React.useEffect(() => {
    fetchCarMakes();
  }, [fetchCarMakes]);

  const selectedMake = watch(makeProperty);
  const makeItem = find(makes, { make: selectedMake });
  const makeId = makeItem ? makeItem.makeId : null;

  React.useEffect(() => {
    if (makeId && !isSelectingMake) {
      fetchCarModels(makeId);
      if (previousMake && previousMake !== selectedMake) {
        setTimeout(() => {
          setValue(modelProperty, '');
        }, 200);
      }
      setTimeout(() => {
        setPreviousMake(selectedMake);
      }, 100);
    }
  }, [
    makeId,
    fetchCarModels,
    setValue,
    modelProperty,
    selectedMake,
    previousMake,
    isSelectingMake,
  ]);

  const getModels = React.useCallback(() => {
    if (makeId && models[makeId]) {
      return models[makeId];
    }
    return [];
  }, [makeId, models]);

  const renderMakeSuggestion = (item) => (
    <div className="suggestion">
      <span>{item.make}</span>
    </div>
  );

  const renderModelSuggestion = (item) => (
    <div className="suggestion">
      <span>{item.model}</span>
    </div>
  );

  return (
    <div className="row">
      <div className="form-group col">
        <Controller
          name={makeProperty}
          control={control}
          render={({ field: { onChange, value } }) => (
            <RequiredWrapper value={value}>
              <SuggestedTextPicker
                input={{
                  value: value || '',
                  onChange: (nextValue) => {
                    setIsSelectingMake(true);
                    onChange(nextValue);
                    setTimeout(() => {
                      setIsSelectingMake(false);
                    }, 1000);
                  },
                }}
                updateOnType={false}
                items={makes}
                renderSuggestion={renderMakeSuggestion}
                placeholder="make"
                matchProperty="make"
                requireSelection
              />
            </RequiredWrapper>
          )}
        />
      </div>
      <div className="form-group col">
        <Controller
          name={modelProperty}
          control={control}
          render={({ field: { onChange, value } }) => (
            <RequiredWrapper value={value}>
              <SuggestedTextPicker
                input={{
                  value: value || '',
                  onChange: (nextValue) => {
                    setValue(modelProperty, nextValue || '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                    if (onChange) {
                      onChange(nextValue || '');
                    }
                  },
                }}
                itemCallback={getModels}
                renderSuggestion={renderModelSuggestion}
                placeholder="model"
                matchProperty="model"
                requireSelection
              />
            </RequiredWrapper>
          )}
        />
      </div>
    </div>
  );
};

CarModelSelect.propTypes = {
  fetchCarMakes: PropTypes.func.isRequired,
  fetchCarModels: PropTypes.func.isRequired,
  makes: PropTypes.arrayOf(
    PropTypes.shape({
      makeId: PropTypes.string.isRequired,
    })
  ),
  models: PropTypes.shape({}),
  makeProperty: PropTypes.string.isRequired,
  modelProperty: PropTypes.string.isRequired,
};

const mapStateToProps = (state) => {
  return {
    makes: selectCarMakes(state),
    models: selectCarModels(state),
  };
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    { fetchCarMakes: fetchCarMakesAction, fetchCarModels: fetchCarModelsAction },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(CarModelSelect);
